import React, { useCallback, useMemo, useEffect, useState } from 'react';

import ConfirmLogoutModalView from './ConfirmLogoutModalView';
import { useMytaverse } from '@providers/MytaverseProvider';

import { sendCustomSentryBreadcrumb } from '@helpers/sentry';

import { IConfirmLogoutModal } from './interfaces';
import { AnalyticsType } from '@interfaces/analytics';
import { useCommunications } from '@core/communications/providers/CommunicationsProvider';
import { shouldShowFeedbackForEvent } from '@core/dashboard/utils/feedbackFormUtils';

const ConfirmLogoutModal = ({
  open,
  onClose,
  cleanAndResetStates = [],
  altStyling = false,
}: IConfirmLogoutModal) => {
  const { logoutUser, setShowFeedbackForm, trackAnalytics, user } =
    useMytaverse();

  const [localEventTags, setLocalEventTags] = useState<string[] | undefined>(
    undefined,
  );

  useEffect(() => {
    const updateEventTags = () => {
      try {
        const storedTags = localStorage.getItem('currentEventTags');
        if (storedTags) {
          const parsedTags = JSON.parse(storedTags);
          setLocalEventTags(parsedTags);
        } else {
          setLocalEventTags(undefined);
        }
      } catch (error) {
        setLocalEventTags(undefined);
      }
    };

    updateEventTags();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'currentEventTags') {
        updateEventTags();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const isInDashboard = !!localEventTags;

  const feedbackCompleted = useMemo(() => user.feedbackCompleted, [user]);

  const shouldShowFeedback = useMemo(() => {
    if (feedbackCompleted) return false;

    if (isInDashboard && localEventTags) {
      return shouldShowFeedbackForEvent(localEventTags);
    }

    return false;
  }, [feedbackCompleted, localEventTags, isInDashboard]);

  const handleAltCancelAction = () => {
    setShowFeedbackForm(true);
    onClose();
  };

  const {
    stopShareCameraToScreen,
    stopScreenSharing,
    isSharingToScreen,
    isSharingScreen,
  } = useCommunications();

  const handleExitButtonClick = useCallback(async () => {
    if (isSharingScreen) {
      await stopScreenSharing();
    }
    if (isSharingToScreen) {
      await stopShareCameraToScreen();
    }

    sendCustomSentryBreadcrumb({ message: 'log out from event' });
    await trackAnalytics(AnalyticsType.LogoutClick);
    logoutUser();
    for (let i = 0; i < cleanAndResetStates.length; i++) {
      cleanAndResetStates[i]();
    }
    onClose();
  }, [
    trackAnalytics,
    logoutUser,
    isSharingScreen,
    isSharingToScreen,
    stopScreenSharing,
    stopShareCameraToScreen,
    cleanAndResetStates,
    onClose,
  ]);

  return (
    <ConfirmLogoutModalView
      open={open}
      onClose={onClose}
      onExitButtonClick={handleExitButtonClick}
      altStyling={altStyling}
      showFeedback={shouldShowFeedback}
      onCancelAltAction={shouldShowFeedback ? handleAltCancelAction : null}
    />
  );
};

export default ConfirmLogoutModal;
